"use client"

import { useState } from "react"
import { Task } from "@/schema"
import { Button, KeyboardShortcut, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@ui/shad"
import { ChevronDown, ChevronUp } from "lucide-react"
import { useHotkeys } from "react-hotkeys-hook"

import TaskDetailSlideover from "./task-detail-slideover"

interface TaskDetailWrapperProps {
  initialTaskId: string
  taskList: Task[]
  initialIndex: number
  onComplete?: (taskId: string | undefined, completed: boolean) => void
}

export function TaskDetailWrapper({ initialTaskId, taskList, initialIndex, onComplete }: TaskDetailWrapperProps) {
  const [currentTaskId, setCurrentTaskId] = useState(initialTaskId)
  const [currentIndex, setCurrentIndex] = useState(initialIndex)

  const handleNavigate = (newIndex: number) => {
    if (newIndex >= 0 && newIndex < taskList.length) {
      const newTask = taskList[newIndex]
      setCurrentTaskId(newTask.id || "")
      setCurrentIndex(newIndex)
    }
  }

  const canNavigatePrevious = currentIndex > 0
  const canNavigateNext = currentIndex < taskList.length - 1

  // Hotkeys for navigation
  useHotkeys("j", () => handleNavigate(currentIndex + 1), [currentIndex])
  useHotkeys("k", () => handleNavigate(currentIndex - 1), [currentIndex])

  return (
    <TooltipProvider>
      {/* Navigation controls - outside the main component so they don't disappear during loading */}
      <div className="absolute -top-11 left-9 flex items-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="iconXs"
              onClick={() => handleNavigate(currentIndex - 1)}
              disabled={!canNavigatePrevious}
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <div className="flex items-center gap-2">
              Previous task
              <KeyboardShortcut keys="K" variant="inverse" />
            </div>
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="iconXs"
              onClick={() => handleNavigate(currentIndex + 1)}
              disabled={!canNavigateNext}
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <div className="flex items-center gap-2">
              Next task
              <KeyboardShortcut keys="J" variant="inverse" />
            </div>
          </TooltipContent>
        </Tooltip>
        <span className="text-muted-foreground ml-2 text-sm">
          {currentIndex + 1} of {taskList.length}
        </span>
      </div>

      {/* Task detail content - this can re-render without affecting navigation */}
      <TaskDetailSlideover key={currentTaskId} taskId={currentTaskId} onComplete={onComplete} />
    </TooltipProvider>
  )
}
